import React from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronRight, Map, ArrowRight, Landmark, Crown, Globe2,
  Swords, Palette, Castle, Briefcase, Database, BookOpen, Scroll, Users
} from 'lucide-react';
import { publications, categoryConfig } from '../data/publications';

const publicationIconMap: Record<string, React.ElementType> = {
  'Landmark': Landmark,
  'Crown': Crown,
  'Globe2': Globe2,
  'Swords': Swords,
  'Palette': Palette,
  'Building2': Castle,
  'Castle': Castle,
  'Map': Map,
  'Briefcase': Briefcase,
  'Database': Database,
};

const stats = [
  { label: 'Publications', value: '72+', icon: BookOpen },
  { label: 'Publication Categories', value: '10', icon: Scroll },
  { label: 'Years of Scholarship', value: '40+', icon: Users },
];

const categoryImages: Record<string, string> = {
  'foundational-histories': '/foundational-histories.jpg.png',
  'governance': '/governance.jpg.png',
  'yoruba-origins': '/yoruba-origins.jpg.png',
  'conflicts': '/conflicts.jpg.png',
  'culture-heritage': '/culture-heritage.jpg.png',
  'urban-development': '/urban-development.jpg.png',
  'palace-tourism': '/palace-tourism.jpg.png',
  'land-property': '/land-property.jpg.png',
  'local-government': '/local-government.jpg.png',
  'reference-data': '/reference-data.jpg.png',
};

export default function HomePage() {
  const categoryPublicationCounts = Object.keys(categoryConfig).reduce((acc, slug) => {
    acc[slug] = publications.filter(pub => pub.category === slug).length;
    return acc;
  }, {} as Record<string, number>);

  return (
    <>
      <div className="relative min-h-[90vh] flex items-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url("https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=2000&q=80")',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#064635]/95 via-[#064635]/90 to-[#064635]/80" />
        </div>

        <div className="relative w-full z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
            <div className="max-w-4xl">
              <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 sm:mb-8 leading-tight">
                Preserving Yoruba Heritage,<br />
                <span className="text-[#D4AF37]">One Page at a Time</span>
              </h1>
              <p className="text-lg sm:text-xl md:text-2xl text-[#D4AF37] mb-4 sm:mb-6 font-medium">
                A Digital Archive of Yoruba History, Heritage & Scholarship
              </p>
              <p className="text-base sm:text-lg md:text-xl text-gray-200 mb-8 sm:mb-12 leading-relaxed max-w-3xl">
                Explore 72+ publications by ESV. Tomori Moshood Adijolola spanning foundational histories,
                traditional governance, cultural heritage, and urban development — from the founding myths
                of Lagelu to modern metropolitan planning.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/publications"
                  className="group bg-[#D4AF37] text-[#064635] px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold
                           flex items-center justify-center hover:bg-[#C49B2C] transition
                           shadow-lg shadow-[#D4AF37]/30 text-base sm:text-lg"
                >
                  Browse Publications
                  <ChevronRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition" />
                </Link>
                <Link
                  to="/about/author"
                  className="bg-white/10 backdrop-blur text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold
                           flex items-center justify-center hover:bg-white/20 border border-white/20
                           transition text-base sm:text-lg"
                >
                  Meet the Author
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent" />
      </div>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div
                  key={index}
                  className="text-center p-8 rounded-xl bg-gradient-to-br from-[#064635]/5 to-[#D4AF37]/5
                           border border-[#064635]/10 hover:shadow-lg transition"
                >
                  <Icon className="h-12 w-12 text-[#064635] mx-auto mb-4" />
                  <div className="text-4xl font-bold text-[#064635] mb-2">{stat.value}</div>
                  <div className="text-gray-700 font-medium">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#064635] mb-6">
              Publication Categories
            </h2>
            <p className="text-lg text-gray-700">
              Explore our comprehensive collection of 72 publications across 10 specialized categories
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {Object.entries(categoryConfig).map(([slug, config]) => {
              const Icon = publicationIconMap[config.icon];
              const count = categoryPublicationCounts[slug];
              const image = categoryImages[slug];

              return (
                <Link
                  key={slug}
                  to={`/collections/${slug}`}
                  className="group relative overflow-hidden rounded-xl shadow-md hover:shadow-2xl
                           transition duration-300 transform hover:-translate-y-1"
                >
                  <div className="aspect-w-16 aspect-h-12">
                    <img
                      src={image}
                      alt={config.title}
                      className="w-full h-64 object-cover group-hover:scale-110 transition duration-500"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-[#064635] via-[#064635]/70 to-transparent">
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <div className="flex items-center justify-between mb-3">
                        <div className="w-10 h-10 rounded-lg bg-[#D4AF37] flex items-center justify-center
                                      transform group-hover:scale-110 transition duration-300">
                          <Icon className="h-5 w-5 text-[#064635]" />
                        </div>
                        <span className="px-3 py-1 bg-[#D4AF37]/20 backdrop-blur-sm rounded-full
                                       text-[#D4AF37] text-sm font-semibold border border-[#D4AF37]/30">
                          {count} {count === 1 ? 'publication' : 'publications'}
                        </span>
                      </div>
                      <h3 className="text-lg font-bold text-white mb-2 group-hover:text-[#D4AF37] transition">
                        {config.title}
                      </h3>
                      <p className="text-gray-300 text-sm leading-relaxed line-clamp-2">
                        {config.description}
                      </p>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/publications"
              className="inline-flex items-center text-[#064635] hover:text-[#D4AF37]
                       font-semibold text-lg group"
            >
              View All Publications
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition" />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#064635] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Preserving Yoruba Heritage Through Scholarship
          </h2>
          <p className="text-xl text-gray-300 mb-10 leading-relaxed">
            Discover decades of research on Ibadan's history, traditional governance, and cultural evolution
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center bg-[#D4AF37] text-[#064635] px-10 py-4 rounded-lg
                     font-semibold text-lg hover:bg-[#C49B2C] transition shadow-lg shadow-[#D4AF37]/30"
          >
            Get in Touch
            <ChevronRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>
    </>
  );
}
